import { useTheme } from "../ThemeContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { fmt, STATUS, DONE_STATUS, ACTIVE_STATUS, PAY_LABELS, PAY_COLORS, valorReal } from "../theme";
import { KpiCard, DataTable, Card, EmptyState, ChartTooltip } from "../components/UI";
import T from "../i18n";

export default function Dashboard({ orders, clock, goToPedidos, lang }) {
  const { theme: C } = useTheme();
  const _ = (k) => T(lang, k);
  const revenue = orders.filter(o => DONE_STATUS.includes(o.status)).reduce((s, o) => s + valorReal(o), 0);
  const active = orders.filter(o => ACTIVE_STATUS.includes(o.status)).length;
  const delivO = orders.filter(o => DONE_STATUS.includes(o.status));
  const avg = delivO.length > 0 ? revenue / delivO.length : 0;

  const hMap = {};
  orders.forEach(o => { if (o.criado_em) { const h = new Date(o.criado_em).getHours(); hMap[h] = (hMap[h] || 0) + 1; } });
  const hourly = Array.from({ length: 14 }, (_, i) => ({ h: `${i + 8}h`, v: hMap[i + 8] || 0 }));

  const payMap = {};
  orders.forEach(o => { payMap[o.forma_pagamento] = (payMap[o.forma_pagamento] || 0) + 1; });
  const payPie = Object.entries(payMap).map(([k, v]) => ({ name: PAY_LABELS[k] || k, value: v, color: PAY_COLORS[k] || C.textMuted }));

  return (
    <div style={{ animation: "fadeUp .4s ease-out" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <div>
          <p style={{ fontSize: 14, color: C.textMuted, marginBottom: 4 }}>{_("bemVindo")}</p>
          <h1 style={{ fontFamily: "'Plus Jakarta Sans'", fontWeight: 800, fontSize: 28 }}>{_("dashboard")}</h1>
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.textSoft, background: C.card, padding: "10px 18px", borderRadius: 10, border: `1px solid ${C.border}`, textTransform: "capitalize" }}>
          {clock.toLocaleDateString(lang === "en" ? "en-US" : lang === "es" ? "es" : "pt-BR", { weekday: "long", day: "numeric", month: "long" })}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }}>
        <KpiCard label={_("faturamento")} value={fmt(revenue)} color={C.green} delay={0} />
        <KpiCard label={_("totalPedidos")} value={orders.length} color={C.blue} delay={0.08} />
        <KpiCard label={_("ticketMedio")} value={fmt(avg)} color={C.orange} delay={0.16} />
        <KpiCard label={_("ativos")} value={active} color={C.red} delay={0.24} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginBottom: 16 }}>
        <Card>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>{_("pedidosPorHora")}</h3>
          <ResponsiveContainer width="100%" height={200}><BarChart data={hourly}><CartesianGrid strokeDasharray="3 3" stroke={C.border} /><XAxis dataKey="h" tick={{ fill: C.textMuted, fontSize: 11 }} axisLine={false} tickLine={false} /><YAxis tick={{ fill: C.textMuted, fontSize: 11 }} axisLine={false} tickLine={false} /><Tooltip content={<ChartTooltip />} /><Bar dataKey="v" radius={[6, 6, 0, 0]} fill={C.red} /></BarChart></ResponsiveContainer>
        </Card>
        <Card>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>{_("pagamentos")}</h3>
          {payPie.length > 0 ? (<><div style={{ display: "flex", justifyContent: "center" }}><PieChart width={180} height={180}><Pie data={payPie} cx={85} cy={85} innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value">{payPie.map((e, i) => <Cell key={i} fill={e.color} />)}</Pie></PieChart></div><div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12, justifyContent: "center" }}>{payPie.map((p, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 10, height: 10, borderRadius: 3, background: p.color }} /><span style={{ fontSize: 12, color: C.textSoft }}>{p.name} ({p.value})</span></div>)}</div></>) : <EmptyState text={_("semDados")} />}
        </Card>
      </div>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700 }}>{_("ultimosPedidos")}</h3>
          <span onClick={goToPedidos} style={{ fontSize: 13, fontWeight: 700, color: C.red, cursor: "pointer" }}>{_("verTodos")}</span>
        </div>
        {orders.length === 0 ? <EmptyState text={_("nenhumPedido")} /> : (
          <DataTable headers={[_("codigo"), _("status"), _("pagamento"), _("total")]}>
            {orders.slice(0, 5).map(o => (
              <tr key={o.id}>
                <td style={{ padding: "12px", fontWeight: 700, fontSize: 13 }}>#{o.codigo}</td>
                <td style={{ padding: "12px" }}><span style={{ fontSize: 11, fontWeight: 700, color: STATUS[o.status]?.color, background: STATUS[o.status]?.bg, padding: "4px 10px", borderRadius: 6 }}>{STATUS[o.status]?.label || o.status}</span></td>
                <td style={{ padding: "12px", fontSize: 13, color: C.textSoft }}>{o.forma_pagamento}</td>
                <td style={{ padding: "12px", fontSize: 14, fontWeight: 800, color: C.green }}>{fmt(valorReal(o))}</td>
              </tr>
            ))}
          </DataTable>
        )}
      </Card>
    </div>
  );
}
